/** The model editor's drawer for one field: label, type, section, flags, options, visibility and roles. */
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { RIGHTS_ROLES, RIGHTS_ROLE_LABELS, fieldIdFromLabel, type FieldDefinition, type FieldType, type MetadataSchemaDefinition, type Role } from '@prism/contracts';
import { useCategories, useRightsSettings, useRoles } from '../../../api/hooks';
import { rightsRolesForType } from '../rightsPolicy';
import { HIDE_FIELD } from './modelDraftOps';

/**
 * 1.8.0: "Used for": the rights role of a field (rights management, Administration → Rights), next to Programme in
 * and out. Shown while rights are on, or when the field already has a rights role; a role is on one field only.
 */
function RightsRoleSelect({ field, def, onChange }: { field: FieldDefinition; def: MetadataSchemaDefinition; onChange: (p: Partial<FieldDefinition>) => void }) {
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  const current = field.role && (RIGHTS_ROLES as readonly string[]).includes(field.role) ? field.role : '';
  const roles = rightsRolesForType(field.type);
  if ((!rightsOn && !current) || (!roles.length && !current)) return null;
  return (
    <div className="field">
      <label htmlFor="fi-rights-role">Used for</label>
      <select id="fi-rights-role" className="select" value={current} onChange={(e) => onChange({ role: (e.target.value || undefined) as typeof field.role })} data-testid="field-role-select">
        <option value="">Nothing special</option>
        {roles.map((r) => <option key={r} value={r} disabled={def.fields.some((f) => f.id !== field.id && f.role === r)}>{RIGHTS_ROLE_LABELS[r]}</option>)}
        {current && !(roles as string[]).includes(current) && <option value={current}>{RIGHTS_ROLE_LABELS[current as keyof typeof RIGHTS_ROLE_LABELS]} (needs another type)</option>}
      </select>
      <span className="help">Rights management reads this field for an asset’s rights: its status, dates, restrictions and credit.</span>
    </div>
  );
}

export const TYPE_LABELS: Record<FieldType, string> = {

  text: 'Text', rich_text: 'Long text', number: 'Number', boolean: 'Yes / no', date: 'Date', datetime: 'Date and time',
  timecode: 'Timecode', vocabulary: 'Controlled vocabulary', tags: 'Keywords',
};

export function FieldInspector({ field, def, isNew, published, onChange, onMove, onRemove }: {
  field: FieldDefinition; def: MetadataSchemaDefinition; isNew: boolean; published: FieldDefinition | null;
  onChange: (p: Partial<FieldDefinition>) => void; onMove: (dir: -1 | 1) => void; onRemove: () => void;
}) {
  const typeChanged = published && published.type !== field.type;
  const others = def.fields.filter((f) => f.id !== field.id && (f.type === 'vocabulary' || f.type === 'boolean'));
  const classes = useCategories().data?.items ?? [];
  const when = field.visibleWhen;
  const dependsOn = def.fields.find((f) => f.id === when?.field);
  const mode: 'always' | 'field' | 'class' | 'both' = !when ? 'always' : when.field && when.classes ? 'both' : when.classes ? 'class' : 'field';
  const defaultFieldCondition = (target: FieldDefinition) => ({ field: target.id, equals: target.type === 'boolean' ? [true] : [target.options?.[0]?.value ?? ''] });
  /** Write the visibility condition; each half is only kept when the chosen mode uses it. */
  const setWhen = (next: { field?: string; equals?: Array<string | boolean>; classes?: string[] } | undefined) => onChange({ visibleWhen: next && (next.field || next.classes) ? next : undefined });
  const locked = field.id === 'f_title';
  return (
    <div className="field-inspector" data-testid="field-inspector">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Read-only: a field's id is permanent once the field exists (the API refuses a change; fixes after rc.9 testing). */}
        <span className="mono faint" data-testid="field-id" aria-label={`Field id ${field.id}, permanent`}
          title="Field id, permanent: stored values, sidecar mappings, searches and workflows refer to this, not the label. Only the label can change">{field.id}</span>
        <span style={{ flex: 1 }} />
        <button className="btn ghost icon sm" aria-label="Move up" onClick={() => onMove(-1)}><ArrowUp size={14} /></button>
        <button className="btn ghost icon sm" aria-label="Move down" onClick={() => onMove(1)}><ArrowDown size={14} /></button>
        {!locked && <button className="btn ghost sm danger" onClick={onRemove}><Trash2 size={13} /> Remove</button>}
      </div>
      <div className="field"><label htmlFor="fi-label">Label</label><input id="fi-label" className="input" value={field.label} onChange={(e) => onChange({ label: e.target.value })} /></div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="fi-type">Type</label>
          <select id="fi-type" className="select" value={field.type} disabled={locked} onChange={(e) => {
            const type = e.target.value as FieldType;
            onChange({ type, ...(type === 'vocabulary' && !field.options?.length ? { options: [{ value: 'option_1', label: 'Option 1' }] } : {}), ...(!['vocabulary', 'tags', 'boolean'].includes(type) ? { facet: undefined } : {}) });
          }}>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="fi-section">Section</label>
          <select id="fi-section" className="select" value={field.section ?? def.sections[0]?.id} onChange={(e) => onChange({ section: e.target.value })}>
            {def.sections.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>
      {typeChanged && !isNew && <div className="banner warning">Changing from {TYPE_LABELS[published!.type]} converts existing values where possible; values that do not fit move to the archive. The review shows exactly how many.</div>}
      <div className="field"><label htmlFor="fi-help">Help text</label><input id="fi-help" className="input" value={field.help ?? ''} onChange={(e) => onChange({ help: e.target.value || undefined })} /></div>

      <div className="toggles">
        <label><input type="checkbox" checked={field.required} disabled={locked} onChange={(e) => onChange({ required: e.target.checked })} /> Required</label>
        <label title={field.required ? 'Required fields block saving when empty, which is stricter than needed' : 'Warn when empty: the asset shows Incomplete metadata, but it can still be saved'}>
          <input type="checkbox" data-testid="field-needed" checked={Boolean(field.needed || field.required)} disabled={locked || field.required || Boolean(field.hidden)}
            onChange={(e) => onChange({ needed: e.target.checked || undefined })} /> Needed
        </label>
        <label title="Show this field in the Library inspector. When no field is flagged, the inspector shows every field that has a value">
          <input type="checkbox" data-testid="field-inspector-flag" checked={Boolean(field.inspector)} disabled={Boolean(field.hidden)}
            onChange={(e) => onChange({ inspector: e.target.checked || undefined })} /> Show in inspector
        </label>
        <label title={['vocabulary', 'tags', 'boolean'].includes(field.type) ? '' : 'Only vocabularies, keywords and yes/no fields can be facets'}>
          <input type="checkbox" checked={Boolean(field.facet)} disabled={!['vocabulary', 'tags', 'boolean'].includes(field.type)} onChange={(e) => onChange({ facet: e.target.checked || undefined })} /> Search facet
        </label>
        <label><input type="checkbox" checked={Boolean(field.column)} onChange={(e) => onChange({ column: e.target.checked || undefined })} /> Table column</label>
        <label title={locked ? 'The title is always shown' : 'Hidden from forms, facets, columns and search; values are kept'}>
          <input type="checkbox" checked={Boolean(field.hidden)} disabled={locked} data-testid="field-hidden"
            onChange={(e) => onChange(e.target.checked ? HIDE_FIELD : { hidden: undefined })} /> Hidden
        </label>
        {field.type === 'vocabulary' && <label><input type="checkbox" checked={Boolean(field.multiple)} onChange={(e) => onChange({ multiple: e.target.checked || undefined })} /> Allow several</label>}
      </div>

      {(field.type === 'text' || field.type === 'rich_text') && (
        <div className="field"><label htmlFor="fi-max">Maximum length</label><input id="fi-max" type="number" min={1} className="input" value={field.maxLength ?? ''} onChange={(e) => onChange({ maxLength: e.target.value ? Number(e.target.value) : undefined })} /></div>
      )}
      {field.type === 'number' && (
        <div className="field-row">
          <div className="field"><label htmlFor="fi-min">Minimum</label><input id="fi-min" type="number" className="input" value={field.min ?? ''} onChange={(e) => onChange({ min: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
          <div className="field"><label htmlFor="fi-maxn">Maximum</label><input id="fi-maxn" type="number" className="input" value={field.max ?? ''} onChange={(e) => onChange({ max: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
        </div>
      )}
      {field.type === 'vocabulary' && <OptionsEditor field={field} onChange={onChange} />}
      {field.type === 'timecode' && (
        <div className="field">
          <label htmlFor="fi-role">Marks</label>
          <select id="fi-role" className="select" value={field.role ?? ''} onChange={(e) => onChange({ role: (e.target.value || undefined) as typeof field.role })}>
            <option value="">Nothing special</option>
            <option value="programme_in" disabled={def.fields.some((f) => f.id !== field.id && f.role === 'programme_in')}>Programme in</option>
            <option value="programme_out" disabled={def.fields.some((f) => f.id !== field.id && f.role === 'programme_out')}>Programme out</option>
          </select>
          <span className="help">Programme in and out show on the player’s timeline, can be set from marked in and out, and exports can keep only that part.</span>
        </div>
      )}
      <RightsRoleSelect field={field} def={def} onChange={onChange} />

      <div className="field">
        <label htmlFor="fi-visible">Show this field</label>
        <select id="fi-visible" className="select" value={mode} onChange={(e) => {
          const m = e.target.value as typeof mode;
          const fieldPart = when?.field && when.equals ? { field: when.field, equals: when.equals } : others[0] ? defaultFieldCondition(others[0]) : undefined;
          const classPart = when?.classes?.length ? when.classes : classes[0] ? [classes[0].id] : undefined;
          if (m === 'always') setWhen(undefined);
          else if (m === 'field') setWhen(fieldPart);
          else if (m === 'class') setWhen(classPart ? { classes: classPart } : undefined);
          else setWhen(fieldPart && classPart ? { ...fieldPart, classes: classPart } : undefined);
        }}>
          <option value="always">Always</option>
          <option value="field" disabled={!others.length}>When field is…</option>
          <option value="class" disabled={!classes.length}>When class is…</option>
          <option value="both" disabled={!others.length || !classes.length}>When field is… and class is…</option>
        </select>
        {(mode === 'field' || mode === 'both') && (
          <>
            <select aria-label="Field this one depends on" className="select" style={{ marginTop: 6 }} value={when?.field ?? ''} data-testid="visible-field" onChange={(e) => {
              const target = def.fields.find((f) => f.id === e.target.value);
              if (target) setWhen({ ...defaultFieldCondition(target), ...(when?.classes ? { classes: when.classes } : {}) });
            }}>
              {others.map((f) => <option key={f.id} value={f.id}>“{f.label}” is…</option>)}
            </select>
            {dependsOn && when && (
              <div className="tags" style={{ marginTop: 6 }}>
                {(dependsOn.type === 'boolean' ? [{ value: true, label: 'Yes' }, { value: false, label: 'No' }] : dependsOn.options ?? []).map((o) => {
                  const equals = when.equals ?? [];
                  const on = equals.includes(o.value as string);
                  return (
                    <button type="button" key={String(o.value)} className={`tag toggle ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => {
                      const eq = on ? equals.filter((v) => v !== o.value) : [...equals, o.value as string | boolean];
                      setWhen({ field: dependsOn.id, equals: eq.length ? eq : equals, ...(when.classes ? { classes: when.classes } : {}) });
                    }}>{o.label}</button>
                  );
                })}
              </div>
            )}
          </>
        )}
        {(mode === 'class' || mode === 'both') && (
          <div className="tags" style={{ marginTop: 6 }} role="group" aria-label="Classes this field is shown for" data-testid="visible-classes">
            {[...classes.map((c) => ({ id: c.id, name: c.name })), ...(when?.classes ?? []).filter((id) => !classes.some((c) => c.id === id)).map((id) => ({ id, name: 'Removed class' }))].map((c) => {
              const list = when?.classes ?? [];
              const on = list.includes(c.id);
              return (
                <button type="button" key={c.id} className={`tag toggle ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => {
                  const next = on ? list.filter((x) => x !== c.id) : [...list, c.id];
                  setWhen({ ...(when?.field ? { field: when.field, equals: when.equals } : {}), classes: next.length ? next : list });
                }}>{c.name}</button>
              );
            })}
          </div>
        )}
        {mode === 'both' && <span className="help">Shown only when both conditions hold.</span>}
        {mode === 'class' && <span className="help">Shown only for assets in the chosen classes (assets with no class do not show it).</span>}
      </div>

      <RolePicker label="Who can see it" value={field.viewRoles} onChange={(v) => onChange({ viewRoles: v, ...(v && field.editRoles ? { editRoles: field.editRoles.filter((r) => v.includes(r)) } : {}) })} />
      <RolePicker label="Who can edit it" value={field.editRoles} onChange={(v) => onChange({ editRoles: v })} allowed={field.viewRoles} editing />
      {field.viewRoles?.length ? <span className="help">Restricted fields are left out of shared search and facets so they never leak to other roles.</span> : null}
    </div>
  );
}

function RolePicker({ label, value, onChange, allowed, editing = false }: { label: string; value: Role[] | undefined; onChange: (v: Role[] | undefined) => void; allowed?: Role[]; editing?: boolean }) {
  const everyone = !value?.length;
  const roles = useRoles().data?.items ?? [];
  // Editing needs asset.edit_metadata, so roles without it are not offered.
  const pool = roles.filter((r) => (editing ? (r.permissions ?? []).includes('asset.edit_metadata') : true)).filter((r) => !allowed?.length || allowed.includes(r.key));
  return (
    <div className="field">
      <label>{label}</label>
      <div className="tags">
        <button type="button" className={`tag toggle ${everyone ? 'on' : ''}`} onClick={() => onChange(undefined)}>{editing ? 'Anyone who can edit metadata' : 'Everyone'}</button>
        {pool.map((r) => {
          const on = !everyone && value!.includes(r.key);
          return (
            <button type="button" key={r.key} className={`tag toggle ${on ? 'on' : ''}`} onClick={() => {
              const next = on ? value!.filter((x) => x !== r.key) : [...(value ?? []), r.key];
              onChange(next.length ? next : undefined);
            }}>{r.name}</button>
          );
        })}
      </div>
    </div>
  );
}

function OptionsEditor({ field, onChange }: { field: FieldDefinition; onChange: (p: Partial<FieldDefinition>) => void }) {
  const options = field.options ?? [];
  const set = (next: typeof options) => onChange({ options: next });
  return (
    <div className="field">
      <label>Options</label>
      <div className="options-editor">
        {options.map((o, i) => (
          <div className="option-row" key={i}>
            <input className="input" aria-label="Option label" value={o.label} onChange={(e) => {
              const next = [...options];
              const wasDerived = o.value === fieldIdFromLabel(o.label, new Set()).slice(2);
              next[i] = { label: e.target.value, value: wasDerived ? fieldIdFromLabel(e.target.value, new Set()).slice(2) : o.value };
              set(next);
            }} />
            <input className="input mono" aria-label="Stored value" value={o.value} onChange={(e) => { const next = [...options]; next[i] = { ...o, value: e.target.value.replace(/\s+/g, '_') }; set(next); }} />
            <button className="btn ghost icon sm" aria-label="Move up" disabled={i === 0} onClick={() => { const next = [...options]; [next[i - 1], next[i]] = [next[i]!, next[i - 1]!]; set(next); }}><ArrowUp size={13} /></button>
            <button className="btn ghost icon sm" aria-label="Remove option" onClick={() => set(options.filter((_, j) => j !== i))}><X size={13} /></button>
          </div>
        ))}
        <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => set([...options, { value: `option_${options.length + 1}`, label: `Option ${options.length + 1}` }])}><Plus size={13} /> Add option</button>
      </div>
      <span className="help">The stored value is what assets keep; labels can change freely. Removing an option archives values that use it.</span>
    </div>
  );
}
